import {motion} from "motion/react";
import {Clock, MessageSquareText, Sparkles, ArrowRight, MessageCircle} from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";

const silkEase = [0.22, 1, 0.36, 1] as const;

const sentenceVariants = {
  hidden: {},
  visible: {
    transition: {staggerChildren: 0.12, delayChildren: 0.1},
  },
};

const wordVariants = {
  hidden: {y: "110%", opacity: 0},
  visible: {
    y: "0%",
    opacity: 1,
    transition: {duration: 0.9, ease: silkEase},
  },
};

export function Hero() {
  const { language } = useLanguage();
  const t = translations[language].hero;

  const words = t.headlineWords;
  const accentWord = t.headlineAccent;

  const stats = [
    { icon: Clock, label: t.stats[0].label, sub: t.stats[0].sub },
    { icon: MessageSquareText, label: t.stats[1].label, sub: t.stats[1].sub },
    { icon: Sparkles, label: t.stats[2].label, sub: t.stats[2].sub },
  ];

  return (
    <section className="min-h-screen px-6 py-16 md:py-24 flex items-center">
      <div className="max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-[55%_45%] gap-16 items-center">
          {/* Left column — text */}
          <div>
            {/* Eyebrow label */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-10"
              style={{
                border: "1px solid var(--border)",
                color: "var(--gold)",
                fontFamily: "var(--font-mono)",
                letterSpacing: "0.10em",
                fontSize: "0.72rem",
                textTransform: "uppercase",
                background: "rgba(212, 149, 106, 0.06)",
              }}
              initial={{opacity: 0, y: 10}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, ease: "easeOut"}}
            >
              <motion.span
                className="w-1.5 h-1.5 rounded-full"
                style={{background: "var(--gold)", display: "inline-block"}}
                animate={{opacity: [1, 0.3, 1]}}
                transition={{duration: 2, repeat: Infinity, ease: "easeInOut"}}
              />
              {t.eyebrow}
            </motion.div>

            {/* H1 — word-by-word clip reveal */}
            <motion.h1
              key={language}
              className="text-5xl md:text-6xl xl:text-7xl mb-8 leading-[1.1]"
              style={{
                fontFamily: "var(--font-display)",
                color: "var(--foreground)",
                letterSpacing: "-0.02em",
              }}
              variants={sentenceVariants}
              initial="hidden"
              animate="visible"
            >
              {words.map((word, i) => (
                <span
                  key={`${word}-${i}`}
                  className="inline-block"
                  style={{marginRight: "0.28em"}}
                >
                  <motion.span className="inline-block" variants={wordVariants}>
                    {word}
                  </motion.span>
                </span>
              ))}
              <span className="inline-block">
                <motion.span
                  className="inline-block"
                  variants={wordVariants}
                  style={{color: "var(--gold)", fontStyle: "italic"}}
                >
                  {accentWord}
                </motion.span>
              </span>
            </motion.h1>

            {/* Paragraph */}
            <motion.p
              className="text-lg md:text-xl leading-relaxed mb-10 max-w-xl"
              style={{color: "var(--foreground-muted)", fontFamily: "var(--font-body)"}}
              initial={{opacity: 0, y: 16}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.5, ease: silkEase}}
            >
              {t.description}
            </motion.p>

            {/* Stat pills */}
            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              {stats.map((stat, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{
                    border: "1px solid var(--border)",
                    background: "var(--surface-1)",
                  }}
                  initial={{opacity: 0, y: 12}}
                  animate={{opacity: 1, y: 0}}
                  transition={{duration: 0.6, delay: 0.7 + i * 0.1, ease: silkEase}}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                      background: "rgba(212, 149, 106, 0.10)",
                      color: "var(--gold)",
                    }}
                  >
                    <stat.icon className="w-4 h-4" strokeWidth={1.5}/>
                  </div>
                  <div>
                    <p
                      className="text-sm font-medium leading-tight"
                      style={{color: "var(--foreground)", fontFamily: "var(--font-body)"}}
                    >
                      {stat.label}
                    </p>
                    <p
                      className="text-xs leading-tight"
                      style={{color: "var(--foreground-muted)", fontFamily: "var(--font-body)"}}
                    >
                      {stat.sub}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-col items-start gap-3">
            <motion.a
              href="#kontakt"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-xl text-base"
              style={{
                background: "linear-gradient(135deg, var(--gold) 0%, var(--gold-bright) 50%, var(--gold) 100%)",
                backgroundSize: "200% 100%",
                color: "#0A0A0B",
                fontFamily: "var(--font-body)",
                fontWeight: 600,
                boxShadow: "0 0 40px var(--glow-gold-strong), 0 4px 24px rgba(0,0,0,0.4)",
                textDecoration: "none",
              }}
              initial={{opacity: 0, y: 12}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.9, ease: silkEase}}
              whileHover={{
                scale: 1.02,
                boxShadow: "0 0 60px var(--glow-gold-strong), 0 8px 32px rgba(0,0,0,0.5)",
              }}
              whileTap={{scale: 0.98}}
            >
              {t.cta}
              <ArrowRight className="w-4 h-4" strokeWidth={2}/>
            </motion.a>
            <motion.p
              className="text-sm"
              style={{color: "var(--foreground-muted)", fontFamily: "var(--font-body)"}}
              initial={{opacity: 0, y: 8}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 1.0, ease: silkEase}}
            >
              {t.ctaNote}
            </motion.p>
            </div>
          </div>

          {/* Right column — floating conversation preview */}
          <motion.div
            className="hidden lg:flex justify-center items-center relative"
            initial={{opacity: 0, x: 20}}
            animate={{opacity: 1, x: 0}}
            transition={{duration: 0.9, delay: 0.3, ease: silkEase}}
          >
            {/* Ambient glow behind the card */}
            <div
              className="absolute -inset-8 rounded-3xl blur-3xl pointer-events-none"
              style={{background: "var(--glow-gold)", opacity: 0.25}}
            />

            <motion.div
              animate={{y: [0, -10, 0]}}
              transition={{duration: 7, repeat: Infinity, ease: "easeInOut"}}
              className="relative w-full max-w-sm"
            >
              <div
                className="rounded-2xl p-6"
                style={{
                  background: "var(--surface-1)",
                  border: "1px solid var(--border)",
                  boxShadow: "0 32px 64px rgba(0,0,0,0.5), 0 0 0 1px var(--border)",
                }}
              >
                {/* Card header */}
                <div
                  className="flex items-center gap-3 mb-6 pb-4"
                  style={{borderBottom: "1px solid var(--border)"}}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                      background: "rgba(212, 149, 106, 0.10)",
                      color: "var(--gold)",
                    }}
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={1.5}/>
                  </div>
                  <div className="flex-1">
                    <p
                      className="text-sm font-medium"
                      style={{color: "var(--cream-dim)", fontFamily: "var(--font-body)"}}
                    >
                      {t.chatHeader}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-emerald-400"/>
                    <motion.span
                      className="text-xs"
                      style={{color: "var(--foreground-muted)", fontFamily: "var(--font-mono)"}}
                    >
                      live
                    </motion.span>
                  </div>
                </div>

                {/* Messages */}
                <div className="space-y-3">
                  {t.chatMessages.map((message, index) => (
                    <motion.div
                      key={index}
                      className={`flex ${message.sender === "client" ? "justify-end" : "justify-start"}`}
                      initial={{opacity: 0, x: message.sender === "client" ? 16 : -16}}
                      animate={{opacity: 1, x: 0}}
                      transition={{duration: 0.5, delay: 1.2 + index * 0.35, ease: silkEase}}
                    >
                      <div
                        className="rounded-2xl px-4 py-2.5 max-w-[85%]"
                        style={
                          message.sender === "client"
                            ? {
                              background: "var(--surface-3)",
                              color: "var(--foreground)",
                              borderBottomRightRadius: "4px",
                              fontFamily: "var(--font-body)",
                              fontSize: "0.8rem",
                              lineHeight: "1.5",
                            }
                            : {
                              background: "rgba(212, 149, 106, 0.14)",
                              border: "1px solid rgba(212, 149, 106, 0.22)",
                              color: "var(--cream)",
                              borderBottomLeftRadius: "4px",
                              fontFamily: "var(--font-mono)",
                              fontSize: "0.76rem",
                              lineHeight: "1.55",
                            }
                        }
                      >
                        <p>{message.text}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
